import DeleteIcon from '../components/DeleteIcon';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import './VerificacaoAndaime.css';

/* ============================================================
   EQUIPAMENTOS — acesso mobile
   Registo de ferramentas e de RMM (equipamentos de monitorização e
   medição) por quem as tem em obra: o que é, marca, modelo e nº de
   série, se existir. Entram em Equipamentos & Ferramentas com o nome
   de quem as carregou e ficam "por etiquetar" até o escritório
   imprimir a etiqueta QR (Equipamentos → 🏷️ Por etiquetar). Até lá,
   quem registou ainda corrige ou apaga o registo.
   ============================================================ */

const CATEGORIAS = [
    {
        v: 'ferramenta', icone: '🔧', titulo: 'Ferramenta', ok: 'Ferramenta registada',
        sub: 'Aparafusadora, berbequim, rebarbadora, serra…', cor: '#2b6cb0', fundo: '#ebf8ff',
    },
    {
        v: 'rmm', icone: '📏', titulo: 'RMM', ok: 'RMM registado',
        sub: 'Equip. de medição: fita métrica, nível, laser…', cor: '#6b46c1', fundo: '#faf5ff',
    },
];
const CAT = Object.fromEntries(CATEGORIAS.map(c => [c.v, c]));
// Um registo que o escritório entretanto passou a "equipamento"
const CAT_OUTRA = { icone: '🚜', titulo: 'Equipamento', ok: 'Registo gravado', cor: '#4a5568', fundo: '#edf2f7' };

// Atalhos para o "O que é?": um toque preenche, o campo continua livre
const SUGESTOES = {
    ferramenta: ['Aparafusadora', 'Berbequim', 'Martelo perfurador', 'Rebarbadora', 'Serra circular', 'Serra de sabre', 'Tico-tico', 'Lixadora'],
    rmm: ['Fita métrica', 'Régua de nível', 'Nível laser', 'Medidor laser', 'Multímetro', 'Detetor de tensão'],
};

const CAMPOS = ['categoria', 'tipo', 'marca', 'modelo', 'n_serie', 'observacoes'];
const FORM_VAZIO = { categoria: '', tipo: '', marca: '', modelo: '', n_serie: '', observacoes: '' };

const fmtData = (v) => (v ? new Date(v).toLocaleDateString('pt-PT') : '');
const fmtHora = (v) => (v ? new Date(v).toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' }) : '');

const textoDuplicado = (d) => {
    const quem = d.registado_por_nome
        ? ` (registada por ${d.registado_por_nome}${d.criado_em ? ` a ${fmtData(d.criado_em)}` : ''})`
        : '';
    return `Já existe «${d.nome}» com o nº de série ${d.n_serie}${quem}.\n\n`
        + 'Se é a mesma ferramenta, não precisa de a registar outra vez.\n\nRegistar mesmo assim?';
};

const MsgErro = ({ texto }) => (texto ? <div role="alert" style={erroCampo}>{texto}</div> : null);

const EquipamentosMobile = () => {
    const navigate = useNavigate();
    const [vista, setVista] = useState('lista');        // lista | form | ok
    const [registos, setRegistos] = useState(null);     // null = a carregar
    const [eu, setEu] = useState('');                   // nome em que os registos ficam
    const [aCarregar, setACarregar] = useState(false);
    const [erroLista, setErroLista] = useState('');
    const [aviso, setAviso] = useState('');             // confirmação curta na lista (ex.: apagado)
    const [form, setForm] = useState(FORM_VAZIO);
    const [emEdicao, setEmEdicao] = useState(null);     // registo a corrigir (null = novo)
    const [erros, setErros] = useState({});
    const [erro, setErro] = useState('');
    const [enviando, setEnviando] = useState(false);
    const aEnviar = useRef(false);                      // trava o duplo toque antes de a página redesenhar
    const [gravado, setGravado] = useState(null);       // o que acabou de ser gravado (ecrã de sucesso)

    const carregar = useCallback(async () => {
        setACarregar(true);
        setErroLista('');
        try {
            // _t: o Safari iOS reutilizava respostas GET antigas na PWA
            const r = await api.get('/registo-ferramentas/meus', { params: { _t: Date.now() } });
            setRegistos(r.data.registos || []);
            setEu(r.data.eu?.nome || '');
        } catch (err) {
            console.error(err);
            setRegistos(prev => prev || []);
            setErroLista('Não foi possível carregar as ferramentas registadas.');
        } finally {
            setACarregar(false);
        }
    }, []);
    useEffect(() => { carregar(); }, [carregar]);

    // Ao voltar à app (PWA/separador), atualizar: a etiqueta pode ter sido impressa entretanto
    useEffect(() => {
        const aoVoltar = () => { if (document.visibilityState === 'visible') carregar(); };
        document.addEventListener('visibilitychange', aoVoltar);
        return () => document.removeEventListener('visibilitychange', aoVoltar);
    }, [carregar]);

    const limparErro = (k) => setErros(x => {
        if (!x[k]) return x;
        const resto = { ...x };
        delete resto[k];
        return resto;
    });
    const set = (k) => (e) => {
        const v = e.target.value;
        setForm(f => ({ ...f, [k]: v }));
        limparErro(k);
    };
    const escolher = (k, v) => {
        setForm(f => ({ ...f, [k]: v }));
        limparErro(k);
    };

    const abrirForm = (dados, registo) => {
        setForm(dados);
        setEmEdicao(registo);
        setErros({});
        setErro('');
        setAviso('');
        setGravado(null);
        setVista('form');
        window.scrollTo({ top: 0 });
    };
    const novo = (categoria = '') => abrirForm({ ...FORM_VAZIO, categoria }, null);
    const corrigir = (r) => abrirForm({
        categoria: CAT[r.categoria] ? r.categoria : '',
        tipo: r.tipo || '', marca: r.marca || '', modelo: r.modelo || '',
        n_serie: r.n_serie || '', observacoes: r.observacoes || '',
    }, r);

    const alterado = emEdicao
        ? CAMPOS.some(k => (form[k] || '') !== (emEdicao[k] || ''))
        : CAMPOS.some(k => k !== 'categoria' && form[k].trim());

    const voltarLista = () => {
        if (vista === 'form' && alterado && !window.confirm('Sair sem gravar o que preencheu?')) return;
        setVista('lista');
        setGravado(null);
        setErro('');
        setErros({});
        window.scrollTo({ top: 0 });
    };

    const validar = () => {
        const e = {};
        if (!CAT[form.categoria]) e.categoria = 'Escolha se é uma ferramenta ou um RMM.';
        if (!form.tipo.trim()) e.tipo = 'Indique o que é.';
        if (!form.marca.trim()) e.marca = 'Indique a marca.';
        if (!form.modelo.trim()) e.modelo = 'Indique o modelo.';
        return e;
    };

    const enviar = (confirmarDuplicado) => {
        const dados = { ...form, confirmar_duplicado: confirmarDuplicado };
        return emEdicao
            ? api.put(`/registo-ferramentas/${emEdicao.id}`, dados)
            : api.post('/registo-ferramentas', dados);
    };

    const gravar = async (e) => {
        e.preventDefault();
        if (aEnviar.current) return;
        const problemas = validar();
        setErros(problemas);
        const chaves = Object.keys(problemas);
        if (chaves.length) {
            setErro('');
            document.getElementById(`ef-${chaves[0]}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            return;
        }
        aEnviar.current = true;
        setEnviando(true);
        setErro('');
        try {
            let r;
            try {
                r = await enviar(false);
            } catch (err) {
                // Nº de série repetido: mostrar qual é o registo que já existe e deixar decidir
                const dup = err.response?.status === 409 && err.response.data?.duplicado;
                if (!dup) throw err;
                if (!window.confirm(textoDuplicado(dup))) {
                    setErro('Não foi gravado: já existe uma ficha com esse nº de série.');
                    return;
                }
                r = await enviar(true);
            }
            setGravado({ ...r.data, corrigido: !!emEdicao });
            setVista('ok');
            carregar();
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } catch (err) {
            console.error(err);
            const resp = err.response;
            if (resp?.data?.error) {
                setErro(resp.data.error);                // a API respondeu com erro: nada ficou gravado
            } else if (!resp || resp.status >= 502) {
                // Sem resposta (rede, timeout): o registo pode ter ficado gravado
                carregar();
                setErro('Não recebemos a confirmação do servidor — pode ter ficado gravado. Veja em «Registadas por mim» antes de voltar a gravar (o que preencheu mantém-se aqui).');
            } else {
                setErro('Não foi possível gravar. Verifique a ligação e tente novamente.');
            }
        } finally {
            aEnviar.current = false;
            setEnviando(false);
        }
    };

    const apagar = async (r) => {
        if (!window.confirm(`Apagar o registo «${r.nome}»?`)) return;
        setAviso('');
        try {
            await api.delete(`/registo-ferramentas/${r.id}`);
            setAviso(`Registo «${r.nome}» apagado.`);
            carregar();
        } catch (err) {
            setErroLista(err.response?.data?.error || 'Não foi possível apagar o registo.');
        }
    };

    // Enter num campo de texto não grava (a tecla "Ir" do telemóvel gravava a meio)
    const bloquearEnter = (e) => {
        if (e.key === 'Enter' && e.target.tagName === 'INPUT') e.preventDefault();
    };

    const botaoCategoria = (c) => {
        const ativo = form.categoria === c.v;
        return (
            <button key={c.v} type="button" aria-pressed={ativo} onClick={() => escolher('categoria', c.v)} style={catBtn(ativo, c)}>
                <span style={{ fontSize: 26, lineHeight: 1 }} aria-hidden="true">{c.icone}</span>
                <span style={{ fontSize: 15, fontWeight: 700, lineHeight: 1.25 }}>{ativo ? '✓ ' : ''}{c.titulo}</span>
                <span style={{ fontSize: 12, fontWeight: 400, lineHeight: 1.3, opacity: 0.85 }}>{c.sub}</span>
            </button>
        );
    };

    const nPorEtiquetar = (registos || []).filter(r => !r.qr_impresso_em).length;
    const titulo = vista === 'form' ? (emEdicao ? '✏️ Corrigir registo' : '🛠️ Registar ferramenta') : '🛠️ Equipamentos';

    return (
        <div className="verificacao-andaimes-container">
            <div className="top-bar">
                <div className="top-bar-left">
                    <div className="top-bar-title">
                        <h1>{titulo}</h1>
                        <p className="mobile-hide">Ferramentas e RMM registados em obra, para a etiqueta QR</p>
                    </div>
                </div>
                <div className="top-bar-actions">
                    {vista === 'lista' ? (
                        <button className="btn-secondary" style={btnTopo} onClick={() => navigate('/mobile-dashboard')}>← Menu</button>
                    ) : (
                        <button className="btn-secondary" style={btnTopo} onClick={voltarLista}>← Voltar</button>
                    )}
                </div>
            </div>

            <div className="page-content" style={{ padding: 16 }}>
                <div className="va-content-wrapper">
                    {vista === 'lista' && (
                        /* ---------- LISTA ---------- */
                        <>
                            <button type="button" onClick={() => novo()} style={btnRegistar}>
                                <span style={iconeRegistar} aria-hidden="true">+</span>
                                <span style={{ minWidth: 0 }}>
                                    <span style={{ display: 'block', fontSize: 17, fontWeight: 800 }}>Registar ferramenta / RMM</span>
                                    <span style={{ display: 'block', fontSize: 13, fontWeight: 400, opacity: 0.9, marginTop: 2 }}>
                                        Marca, modelo e nº de série — para a etiqueta QR
                                    </span>
                                </span>
                            </button>

                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                                <div style={{ minWidth: 0 }}>
                                    <div style={{ fontWeight: 700, fontSize: 15, color: '#1a202c' }}>
                                        Registadas por mim{registos?.length ? ` (${registos.length})` : ''}
                                    </div>
                                    {nPorEtiquetar > 0 && (
                                        <div style={{ fontSize: 13, color: '#c05621', marginTop: 2 }}>🏷️ {nPorEtiquetar} por etiquetar</div>
                                    )}
                                </div>
                                <button type="button" onClick={carregar} disabled={aCarregar} style={btnMini}>
                                    {aCarregar ? 'A atualizar…' : '🔄 Atualizar'}
                                </button>
                            </div>

                            {aviso && <div className="va-message success">{aviso}</div>}
                            {erroLista && <div className="va-message error">{erroLista}</div>}

                            {registos === null ? (
                                <div style={vazio}>A carregar…</div>
                            ) : registos.length === 0 ? (
                                <div style={vazio}>
                                    Ainda não registou nenhuma ferramenta.<br />
                                    Toque em «Registar ferramenta / RMM» para começar.
                                </div>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                    {registos.map(r => {
                                        const c = CAT[r.categoria] || CAT_OUTRA;
                                        return (
                                            <div key={r.id} style={{ ...cartao, marginBottom: 0, borderLeft: `4px solid ${c.cor}` }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                                    <span style={badge(c.fundo, c.cor)}>{c.icone} {c.titulo}</span>
                                                    {r.qr_impresso_em
                                                        ? <span style={{ ...badge('#f0fff4', '#276749'), marginLeft: 'auto' }}>✅ Etiquetada</span>
                                                        : <span style={{ ...badge('#fffaf0', '#c05621'), marginLeft: 'auto' }}>🏷️ Por etiquetar</span>}
                                                </div>
                                                <div style={{ fontWeight: 700, fontSize: 16, color: '#1a202c', marginTop: 8, wordBreak: 'break-word' }}>
                                                    {r.nome}
                                                </div>
                                                <div style={{ fontSize: 14, color: '#4a5568', marginTop: 4, wordBreak: 'break-word' }}>
                                                    {r.n_serie ? <>Nº de série: <b>{r.n_serie}</b></> : 'Sem nº de série'}
                                                </div>
                                                {r.observacoes && (
                                                    <div style={{ fontSize: 13, color: '#718096', marginTop: 4, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                                        {r.observacoes}
                                                    </div>
                                                )}
                                                <div style={{ fontSize: 12, color: '#718096', marginTop: 6 }}>
                                                    Registada a {fmtData(r.criado_em)} às {fmtHora(r.criado_em)}
                                                    {r.qr_impresso_em ? ` · etiqueta impressa a ${fmtData(r.qr_impresso_em)}` : ''}
                                                </div>
                                                {!r.qr_impresso_em && (
                                                    <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                                                        <button type="button" style={btnAcao} onClick={() => corrigir(r)}>✏️ Corrigir</button>
                                                        <button type="button" style={btnApagar} onClick={() => apagar(r)}><DeleteIcon /> Apagar</button>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </>
                    )}

                    {vista === 'ok' && gravado && (
                        /* ---------- GRAVADO ---------- */
                        <div className="va-success-actions">
                            <div style={{ fontSize: 44, lineHeight: 1 }}>✅</div>
                            <p className="success-text" style={{ margin: '12px 0 6px' }}>
                                {gravado.corrigido ? 'Registo corrigido' : (CAT[gravado.categoria] || CAT_OUTRA).ok}
                            </p>
                            <div style={{ fontSize: 20, fontWeight: 800, color: '#1a202c', wordBreak: 'break-word' }}>{gravado.nome}</div>
                            <div style={{ color: '#4a5568', marginTop: 4, wordBreak: 'break-word' }}>
                                {gravado.n_serie ? `Nº de série ${gravado.n_serie}` : 'Sem nº de série'}
                            </div>
                            <p style={{ color: '#4a5568', margin: '14px 0 16px', lineHeight: 1.45 }}>
                                🏷️ Fica <b>por etiquetar</b> até o escritório imprimir a etiqueta QR.
                                Até lá ainda pode corrigir o registo em «Registadas por mim».
                            </p>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                <button type="button" className="va-action-btn" onClick={() => novo(CAT[gravado.categoria] ? gravado.categoria : '')}
                                    style={{ background: '#0056b3', color: '#fff', width: '100%', padding: '14px 20px' }}>
                                    ➕ Registar outra
                                </button>
                                <button type="button" className="va-action-btn secondary-btn" onClick={voltarLista}
                                    style={{ width: '100%', padding: '14px 20px' }}>
                                    📋 Registadas por mim
                                </button>
                            </div>
                        </div>
                    )}

                    {vista === 'form' && (
                        /* ---------- REGISTAR / CORRIGIR ---------- */
                        <form className="va-form" onSubmit={gravar} onKeyDown={bloquearEnter} noValidate>
                            {emEdicao && (
                                <div style={notaInfo}>
                                    A corrigir o registo de {fmtData(emEdicao.criado_em)}. Pode corrigi-lo enquanto a etiqueta QR não for impressa.
                                </div>
                            )}

                            <div id="ef-categoria" role="group" aria-labelledby="ef-tit-categoria" style={cartaoE(!!erros.categoria)}>
                                <div id="ef-tit-categoria" style={secTit}>É uma ferramenta ou um RMM? *</div>
                                <div style={grelha2}>{CATEGORIAS.map(botaoCategoria)}</div>
                                <MsgErro texto={erros.categoria} />
                            </div>

                            <div style={cartao}>
                                <label htmlFor="ef-tipo" style={secTitRotulo}>O que é? *</label>
                                {CAT[form.categoria] && (
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
                                        {SUGESTOES[form.categoria].map(s => (
                                            <button key={s} type="button" onClick={() => escolher('tipo', s)}
                                                style={chip(form.tipo.trim().toLowerCase() === s.toLowerCase())}>
                                                {s}
                                            </button>
                                        ))}
                                    </div>
                                )}
                                <input id="ef-tipo" value={form.tipo} onChange={set('tipo')} maxLength={100}
                                    placeholder={form.categoria === 'rmm' ? 'Ex.: fita métrica' : 'Ex.: aparafusadora'}
                                    autoComplete="off" style={{ ...inp, ...(erros.tipo ? inpComErro : {}) }} />
                                <MsgErro texto={erros.tipo} />
                            </div>

                            <div style={cartao}>
                                <div style={secTit}>Identificação</div>

                                <label htmlFor="ef-marca" style={rotulo}>Marca *</label>
                                <input id="ef-marca" value={form.marca} onChange={set('marca')} maxLength={100}
                                    placeholder="Ex.: Bosch" autoComplete="off" autoCapitalize="words"
                                    style={{ ...inp, ...(erros.marca ? inpComErro : {}) }} />
                                <MsgErro texto={erros.marca} />

                                <label htmlFor="ef-modelo" style={{ ...rotulo, marginTop: 14 }}>Modelo *</label>
                                <input id="ef-modelo" value={form.modelo} onChange={set('modelo')} maxLength={120}
                                    placeholder="Ex.: GSB 18V-21" autoComplete="off" autoCapitalize="characters"
                                    autoCorrect="off" spellCheck={false}
                                    style={{ ...inp, ...(erros.modelo ? inpComErro : {}) }} />
                                <MsgErro texto={erros.modelo} />

                                <label htmlFor="ef-n_serie" style={{ ...rotulo, marginTop: 14 }}>
                                    Nº de série <span style={opcional}>(se existir)</span>
                                </label>
                                <input id="ef-n_serie" value={form.n_serie} onChange={set('n_serie')} maxLength={100}
                                    autoComplete="off" autoCapitalize="characters" autoCorrect="off" spellCheck={false}
                                    style={inp} />
                                <div style={dica}>Costuma estar na chapa ou na etiqueta da ferramenta (S/N). Se não tiver, deixe em branco.</div>
                            </div>

                            <div style={cartao}>
                                <label htmlFor="ef-observacoes" style={secTitRotulo}>
                                    Observações <span style={opcional}>(opcional)</span>
                                </label>
                                <textarea id="ef-observacoes" value={form.observacoes} onChange={set('observacoes')} rows={3} maxLength={1000}
                                    placeholder="Ex.: com 2 baterias e carregador"
                                    style={{ ...inp, minHeight: 80, resize: 'vertical' }} />
                            </div>

                            {eu && !emEdicao && (
                                <div style={notaInfo}>👤 Fica registada em nome de <b>{eu}</b>.</div>
                            )}
                            {erro && <div className="va-message error" role="alert">{erro}</div>}

                            <div className="va-actions" style={{ marginTop: 6 }}>
                                <button type="submit" className="va-submit-btn" disabled={enviando}
                                    style={{ padding: '16px 20px', width: '100%' }}>
                                    {enviando ? 'A gravar…' : emEdicao ? '💾 Gravar correção' : '💾 Registar'}
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
};

export default EquipamentosMobile;

/* ---------- estilos (os mesmos do registo de incidentes) ---------- */
const cartao = { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 14, marginBottom: 14, boxShadow: '0 1px 2px rgba(0,0,0,0.04)' };
const cartaoE = (comErro) => (comErro ? { ...cartao, border: '1px solid #fc8181', boxShadow: '0 0 0 1px #fc8181' } : cartao);
const secTit = { fontWeight: 700, fontSize: 15, color: '#1a202c', marginBottom: 10 };
const secTitRotulo = { ...secTit, display: 'block' };  // título do cartão que é o rótulo do próprio campo
const rotulo = { display: 'block', fontWeight: 600, marginBottom: 8, fontSize: '0.95rem', color: '#444' };
const opcional = { fontWeight: 400, fontSize: 13, color: '#718096' };
const dica = { fontSize: 12, color: '#718096', marginTop: 6, lineHeight: 1.35 };
const grelha2 = { display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 10 };
// fontSize 16: abaixo disso o Safari do iPhone faz zoom ao tocar no campo
const inp = {
    display: 'block', width: '100%', minWidth: 0, boxSizing: 'border-box', minHeight: 44, fontSize: 16,
    padding: '10px 12px', border: '1px solid #cbd5e0', borderRadius: 8, background: '#fff', color: '#2d3748', fontFamily: 'inherit',
};
const inpComErro = { border: '1px solid #e53e3e', boxShadow: '0 0 0 1px #e53e3e' };
const erroCampo = { color: '#c53030', fontSize: 13, fontWeight: 600, marginTop: 6, lineHeight: 1.35 };
const chip = (ativo) => ({
    minHeight: 44, padding: '9px 14px', borderRadius: 22, fontWeight: 600, fontSize: 14, cursor: 'pointer', fontFamily: 'inherit',
    border: '1px solid ' + (ativo ? '#2b6cb0' : '#cbd5e0'),
    background: ativo ? '#2b6cb0' : '#fff', color: ativo ? '#fff' : '#334155',
});
const catBtn = (ativo, c) => ({
    display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 5, width: '100%', minWidth: 0, minHeight: 104,
    textAlign: 'left', overflowWrap: 'break-word', wordBreak: 'break-word',
    padding: '12px 10px', borderRadius: 12, cursor: 'pointer', fontFamily: 'inherit',
    border: `2px solid ${ativo ? c.cor : '#e2e8f0'}`, background: ativo ? c.fundo : '#fff',
    color: ativo ? c.cor : '#2d3748', boxShadow: ativo ? `0 0 0 3px ${c.cor}22` : 'none',
});
const badge = (bg, fg) => ({ padding: '3px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600, background: bg, color: fg, whiteSpace: 'nowrap' });
const btnTopo = { minHeight: 44, whiteSpace: 'nowrap' };
// O mesmo azul dos cartões do menu mobile: é "o botão" desta página
const btnRegistar = {
    display: 'flex', alignItems: 'center', gap: 14, width: '100%', textAlign: 'left', cursor: 'pointer',
    padding: '16px 18px', marginBottom: 18, border: 'none', borderRadius: 14, fontFamily: 'inherit',
    background: 'linear-gradient(135deg, #3182ce 0%, #2563a8 100%)', color: '#fff',
    boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
};
// Caixa do ícone como a dos cartões do menu; "+" em texto fica branco em qualquer telemóvel
const iconeRegistar = {
    width: 44, height: 44, flexShrink: 0, borderRadius: 12, background: 'rgba(255,255,255,0.22)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 30, fontWeight: 700, lineHeight: 1,
};
const btnMini = { border: '1px solid #cbd5e0', background: '#fff', borderRadius: 8, padding: '8px 14px', minHeight: 44, fontSize: 13, cursor: 'pointer', flexShrink: 0 };
const btnAcao = {
    flex: 1, minHeight: 44, border: '1px solid #cbd5e0', background: '#fff', color: '#2d3748',
    borderRadius: 8, fontWeight: 600, fontSize: 14, cursor: 'pointer', fontFamily: 'inherit',
};
const btnApagar = { ...btnAcao, border: '1px solid #feb2b2', background: '#fff5f5', color: '#c53030' };
const notaInfo = {
    background: '#f7fafc', border: '1px solid #e2e8f0', color: '#4a5568', borderRadius: 10,
    padding: '10px 12px', fontSize: 14, lineHeight: 1.45, marginBottom: 14,
};
const vazio = { color: '#94a3b8', fontSize: 14, textAlign: 'center', padding: '28px 0', lineHeight: 1.5 };
